import React from 'react';
import PropTypes from 'prop-types';
import {
  LuCaptions,
  LuCaptionsOff,
  LuMaximize,
  LuMinimize,
  LuPause,
  LuPlay,
  LuRotateCcw,
  LuSkipBack,
  LuSkipForward,
  LuVolume2,
  LuVolumeX,
} from 'react-icons/lu';
import { formatClock } from './usePlayback';

const SPEEDS = [1, 1.25, 1.5, 0.75];

const ControlButton = ({ label, onClick, disabled, active, dark, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={label}
    aria-label={label}
    aria-pressed={active === undefined ? undefined : active}
    className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-transparent tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/60 disabled:tw-cursor-not-allowed disabled:tw-opacity-40 ${
      dark
        ? 'tw-text-white/80 hover:tw-bg-white/10 hover:tw-text-white'
        : 'tw-text-slate-600 hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-900 dark:tw-text-slate-300 dark:hover:tw-bg-white/10 dark:hover:tw-text-white'
    }`}
  >
    {children}
  </button>
);

ControlButton.propTypes = { label: PropTypes.string.isRequired, onClick: PropTypes.func.isRequired, disabled: PropTypes.bool, active: PropTypes.bool, dark: PropTypes.bool, children: PropTypes.node };

/**
 * Transport bar: play / pause, chapter skip, a scrubber with chapter marks
 * (a native range input underneath, so it works with keyboard and touch),
 * speed, captions, narration and full screen. `label(key)` gives the texts.
 */
const GuideControls = ({ label, timeline, time, playing, ended, rate, captionsOn, voiceOn, voiceAvailable, fullscreen, onTogglePlay, onSeek, onPrevChapter, onNextChapter, onRate, onCaptions, onVoice, onFullscreen }) => {
  const percent = (time / timeline.total) * 100;
  const dark = fullscreen;

  return (
    <div className={`tw-space-y-2 tw-rounded-2xl tw-px-2 tw-py-2 ${dark ? 'tw-w-full tw-bg-white/[0.06]' : 'tw-border tw-border-slate-200/80 tw-bg-white/80 dark:tw-border-white/10 dark:tw-bg-white/[0.03]'}`}>
      <div className="tw-relative tw-mx-2 tw-h-5">
        <span className={`tw-absolute tw-inset-x-0 tw-top-1/2 tw-h-1.5 -tw-translate-y-1/2 tw-overflow-hidden tw-rounded-full ${dark ? 'tw-bg-white/20' : 'tw-bg-slate-200 dark:tw-bg-white/10'}`}>
          <span className="tw-block tw-h-full tw-rounded-full tw-bg-gradient-to-r tw-from-teal-400 tw-to-cyan-500" style={{ width: `${percent}%` }} />
        </span>
        {timeline.chapters.slice(1).map((chapter) => (
          <span key={chapter.key} style={{ left: `${(chapter.start / timeline.total) * 100}%` }} className={`tw-absolute tw-top-1/2 tw-h-3 tw-w-0.5 -tw-translate-y-1/2 tw-rounded-full ${dark ? 'tw-bg-white/50' : 'tw-bg-white dark:tw-bg-[#1f232b]'}`} />
        ))}
        <span style={{ left: `${percent}%` }} className="tw-pointer-events-none tw-absolute tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-rounded-full tw-border-2 tw-border-white tw-bg-teal-500 tw-shadow" />
        <input
          type="range"
          min={0}
          max={timeline.total}
          step={250}
          value={time}
          onChange={(event) => onSeek(Number(event.target.value))}
          aria-label={label('seek')}
          aria-valuetext={`${formatClock(time)} / ${formatClock(timeline.total)}`}
          className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-cursor-pointer tw-appearance-none tw-bg-transparent tw-opacity-0"
        />
      </div>

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1">
        <button
          type="button"
          onClick={onTogglePlay}
          aria-label={ended ? label('replay') : playing ? label('pause') : label('play')}
          title={ended ? label('replay') : playing ? label('pause') : label('play')}
          className="tw-grid tw-h-11 tw-w-11 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-b tw-from-teal-400 tw-to-cyan-600 tw-text-white tw-shadow-lg tw-shadow-teal-500/30 tw-transition-transform active:tw-scale-95 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/60"
        >
          {ended ? <LuRotateCcw className="tw-h-5 tw-w-5" /> : playing ? <LuPause className="tw-h-5 tw-w-5" /> : <LuPlay className="tw-ml-0.5 tw-h-5 tw-w-5" />}
        </button>
        <ControlButton label={label('prevChapter')} onClick={onPrevChapter} dark={dark}><LuSkipBack className="tw-h-[18px] tw-w-[18px]" /></ControlButton>
        <ControlButton label={label('nextChapter')} onClick={onNextChapter} dark={dark}><LuSkipForward className="tw-h-[18px] tw-w-[18px]" /></ControlButton>
        <span className={`tw-ml-1 tw-text-xs tw-font-semibold tw-tabular-nums ${dark ? 'tw-text-white/80' : 'tw-text-slate-600 dark:tw-text-slate-300'}`}>
          {formatClock(time)} / {formatClock(timeline.total)}
        </span>
        <span className="tw-flex-1" />
        <button
          type="button"
          onClick={() => onRate(SPEEDS[(SPEEDS.indexOf(rate) + 1) % SPEEDS.length])}
          title={label('speed')}
          aria-label={`${label('speed')}: ${rate}×`}
          className={`tw-h-8 tw-rounded-full tw-bg-transparent tw-px-2.5 tw-text-xs tw-font-bold tw-tabular-nums tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/60 ${dark ? 'tw-text-white/80 hover:tw-bg-white/10' : 'tw-text-slate-600 hover:tw-bg-slate-900/[0.06] dark:tw-text-slate-300 dark:hover:tw-bg-white/10'}`}
        >
          {rate}×
        </button>
        <ControlButton label={captionsOn ? label('captionsOn') : label('captionsOff')} onClick={onCaptions} active={captionsOn} dark={dark}>
          {captionsOn ? <LuCaptions className="tw-h-[18px] tw-w-[18px]" /> : <LuCaptionsOff className="tw-h-[18px] tw-w-[18px]" />}
        </ControlButton>
        <ControlButton
          label={!voiceAvailable ? label('voiceUnavailable') : voiceOn ? label('voiceOn') : label('voiceOff')}
          onClick={onVoice}
          disabled={!voiceAvailable}
          active={voiceAvailable ? voiceOn : undefined}
          dark={dark}
        >
          {voiceOn && voiceAvailable ? <LuVolume2 className="tw-h-[18px] tw-w-[18px]" /> : <LuVolumeX className="tw-h-[18px] tw-w-[18px]" />}
        </ControlButton>
        <ControlButton label={fullscreen ? label('exitFullscreen') : label('fullscreen')} onClick={onFullscreen} dark={dark}>
          {fullscreen ? <LuMinimize className="tw-h-[18px] tw-w-[18px]" /> : <LuMaximize className="tw-h-[18px] tw-w-[18px]" />}
        </ControlButton>
      </div>
    </div>
  );
};

GuideControls.propTypes = {
  label: PropTypes.func.isRequired,
  timeline: PropTypes.object.isRequired,
  time: PropTypes.number.isRequired,
  playing: PropTypes.bool.isRequired,
  ended: PropTypes.bool.isRequired,
  rate: PropTypes.number.isRequired,
  captionsOn: PropTypes.bool.isRequired,
  voiceOn: PropTypes.bool.isRequired,
  voiceAvailable: PropTypes.bool.isRequired,
  fullscreen: PropTypes.bool.isRequired,
  onTogglePlay: PropTypes.func.isRequired,
  onSeek: PropTypes.func.isRequired,
  onPrevChapter: PropTypes.func.isRequired,
  onNextChapter: PropTypes.func.isRequired,
  onRate: PropTypes.func.isRequired,
  onCaptions: PropTypes.func.isRequired,
  onVoice: PropTypes.func.isRequired,
  onFullscreen: PropTypes.func.isRequired,
};

export default GuideControls;
